import React, { useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';

export const REQUEST_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
export const REQUEST_ATTACHMENT_EXTENSIONS = [
  '.zip', '.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp',
  '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
];

export const formatAttachmentSize = (bytes) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const fileExtension = (name) => {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
};

const RequestAttachmentPicker = ({
  files,
  onChange,
  disabled = false,
  // Rendered inside the drop zone so a composer's own inputs accept drops too,
  // instead of the browser opening the dropped file and losing the page state.
  children = null,
}) => {
  const inputRef = useRef(null);
  // Drag events fire again for every child element, so nesting is tracked by
  // depth rather than a plain boolean.
  const dragDepth = useRef(0);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const acceptsFiles = !disabled;

  const addFiles = (incoming) => {
    setError('');
    if (incoming.length === 0) return;

    const invalidType = incoming.find(
      (file) => !REQUEST_ATTACHMENT_EXTENSIONS.includes(fileExtension(file.name))
    );
    if (invalidType) {
      setError(`${invalidType.name} is not a supported file type.`);
      return;
    }
    const tooLarge = incoming.find(
      (file) => file.size > REQUEST_ATTACHMENT_MAX_BYTES
    );
    if (tooLarge) {
      setError(`${tooLarge.name} exceeds the 25 MB limit.`);
      return;
    }
    const combined = [...files];
    incoming.forEach((file) => {
      const duplicate = combined.some(
        (item) =>
          item.name === file.name &&
          item.size === file.size &&
          item.lastModified === file.lastModified
      );
      if (!duplicate) combined.push(file);
    });
    onChange(combined);
  };

  const handleInputChange = (event) => {
    const incoming = Array.from(event.target.files || []);
    event.target.value = '';
    addFiles(incoming);
  };

  const isFileDrag = (event) =>
    Array.from(event.dataTransfer?.types || []).includes('Files');

  const handleDragEnter = (event) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    dragDepth.current += 1;
    setError('');
    setIsDragging(true);
  };

  const handleDragOver = (event) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = acceptsFiles ? 'copy' : 'none';
  };

  const handleDragLeave = (event) => {
    if (!isFileDrag(event)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  };

  const handleDrop = (event) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);

    if (disabled) return;

    // Entries must be read synchronously while the drop event is live.
    const droppedDirectory = Array.from(event.dataTransfer.items || []).some(
      (item) => item.webkitGetAsEntry?.()?.isDirectory
    );
    if (droppedDirectory) {
      setError('Folders cannot be attached. Zip the folder and drop the zip instead.');
      return;
    }

    addFiles(Array.from(event.dataTransfer.files || []));
  };

  const dropHint = acceptsFiles
    ? 'Drop files to attach them'
    : 'Files cannot be added right now.';

  return (
    <div
      className={`request-attachment-picker${isDragging ? ' dragging' : ''}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children && <div className="request-attachment-slot">{children}</div>}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={REQUEST_ATTACHMENT_EXTENSIONS.join(',')}
        onChange={handleInputChange}
        disabled={!acceptsFiles}
        className="request-attachment-input"
      />
      <button
        type="button"
        className="garage-btn garage-btn-secondary request-attachment-button"
        onClick={() => inputRef.current?.click()}
        disabled={!acceptsFiles}
      >
        <Paperclip size={16} />
        Add files
      </button>
      {isDragging ? (
        <span className="request-attachment-hint drop-active">{dropHint}</span>
      ) : (
        <span className="request-attachment-hint">
          Drag and drop or browse · Zip, PDF, images, or Office files · 25 MB each
        </span>
      )}
      {error && <div className="request-attachment-error">{error}</div>}
      {files.length > 0 && (
        <div className="request-attachment-selected">
          {files.map((file, index) => (
            <div
              className="request-attachment-chip"
              key={`${file.name}-${file.size}-${file.lastModified}`}
            >
              <Paperclip size={14} />
              <span title={file.name}>{file.name}</span>
              <small>{formatAttachmentSize(file.size)}</small>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                onClick={() => onChange(files.filter((_, itemIndex) => itemIndex !== index))}
                disabled={disabled}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RequestAttachmentPicker;
